import { useCallback, useEffect, useMemo, useState } from "react";
import { Crown } from "lucide-react";
import DriverCard from "@/components/DriverCard";
import { playReveal, playStageReveal } from "@/lib/sfx";

/**
 * Walkout-Reveal-System für Driver Cards.
 *
 * Props:
 *   reveals    – [{ driver, prev?, team?, oldOvr, newOvr }]
 *   onClose    – callback
 *   autoMs     – ms bis Auto-Advance (0 = aus). Default 6000.
 *
 * Tier-Hierarchie (revealMode = true):
 *   driver.is_champion → CHAMPION   (Lila Fireworks-Explosion, Screen-Shake, Crown-Banner)
 *   ovr ≥ 90           → LEGENDARY  (Massive Gold-Explosion, 8 Beams, Sunrays, Mega-Konfetti)
 *   ovr ≥ 86 (≈WG)     → WALKOUT    (Sparks + Lightning + Flash + Light-Konfetti)
 *   ovr ≥ 76           → UPGRADE
 *   sonst              → STANDARD
 *
 * Delta-Modus (revealMode = false): downgrade / legendary (Δ≥10) / walkout (Δ≥5) / upgrade (Δ≥3) / standard
 *
 * Steuerung: Klick / Space / →  = nächster. Esc = schließen.
 */
export default function WalkoutReveal({ reveals = [], onClose, autoMs = 6000, revealMode = false }) {
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState("enter"); // "enter" | "settled" | "exit"
  const [stageStep, setStageStep] = useState(5); // 0=alles verdeckt → 5=alles offen (nur legendary)

  const current = reveals[idx];
  const total = reveals.length;
  const delta = current ? (current.newOvr || 0) - (current.oldOvr || 0) : 0;

  const tier = useMemo(() => {
    // Champion-Flag schlägt alles
    if (current?.driver?.is_champion) return "champion";
    if (revealMode) {
      const ovr = current?.newOvr || current?.driver?.ovr || 0;
      if (ovr >= 90) return "legendary";
      if (ovr >= 86) return "walkout";
      if (ovr >= 76) return "upgrade";
      return "standard";
    }
    if (delta <= -1) return "downgrade";
    if (delta >= 10) return "legendary";
    if (delta >= 5) return "walkout";
    if (delta >= 3) return "upgrade";
    return "standard";
  }, [delta, revealMode, current]);

  const next = useCallback(() => {
    setPhase("exit");
    setTimeout(() => {
      if (idx + 1 < total) {
        setIdx(i => i + 1);
        setPhase("enter");
      } else {
        onClose?.();
      }
    }, 320);
  }, [idx, total, onClose]);

  useEffect(() => {
    if (phase !== "enter") return;
    try { playReveal(tier); } catch { /* noop */ }
    const t = setTimeout(() => setPhase("settled"), 1400);
    return () => clearTimeout(t);
  }, [phase, idx, tier]);

  // Gestaffelter Reveal nur für legendary (OVR ≥ 90):
  // 0=alles verdeckt → 1=Werte → 2=OVR → 3=Land → 4=Team → 5=Bild+Name
  // ACHTUNG: `phase` darf NICHT in deps, sonst killt der enter→settled Flip die Timeouts.
  useEffect(() => {
    if (tier !== "legendary") {
      setStageStep(5);
      return;
    }
    setStageStep(0);
    const timeouts = [
      setTimeout(() => { setStageStep(1); try { playStageReveal(1); } catch { /* noop */ } }, 1200),
      setTimeout(() => { setStageStep(2); try { playStageReveal(2); } catch { /* noop */ } }, 2700),
      setTimeout(() => { setStageStep(3); try { playStageReveal(3); } catch { /* noop */ } }, 4300),
      setTimeout(() => { setStageStep(4); try { playStageReveal(4); } catch { /* noop */ } }, 6000),
      setTimeout(() => { setStageStep(5); try { playStageReveal(5); } catch { /* noop */ } }, 8000),
    ];
    return () => timeouts.forEach(clearTimeout);
  }, [idx, tier]);

  useEffect(() => {
    if (!autoMs || phase !== "settled") return;
    const t = setTimeout(next, autoMs);
    return () => clearTimeout(t);
  }, [phase, idx, autoMs, next]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { onClose?.(); }
      else if (e.key === " " || e.key === "Enter" || e.key === "ArrowRight") {
        e.preventDefault();
        next();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, onClose]);

  if (!current) return null;

  // Tier-Farb-Mapping
  const tierColor =
    tier === "champion" ? "#A855F7" :
    tier === "legendary" ? "#FFD700" :
    tier === "walkout" ? "#FFC83A" :
    tier === "upgrade" ? "#FACC15" :
    tier === "downgrade" ? "#FF3B30" :
    "#00E5FF";
  const tierColorAlt =
    tier === "champion" ? "#E9D5FF" :
    tier === "legendary" ? "#FFF3B0" :
    tier === "walkout" ? "#FFE9A8" :
    "#FFFFFF";

  // Effekt-Aktivierungs-Flags pro Tier
  const isMega = tier === "champion" || tier === "legendary";
  const hasFireworks = tier === "champion";
  const hasSunrays = tier === "champion" || tier === "legendary";
  const hasShake = tier === "champion" || tier === "legendary";
  const hasLightning = isMega || tier === "walkout";
  const lightningCount = tier === "champion" ? 10 : tier === "legendary" ? 8 : tier === "walkout" ? 5 : 0;
  const hasConfetti = isMega || tier === "walkout";
  const confettiCount = tier === "champion" ? 80 : tier === "legendary" ? 60 : tier === "walkout" ? 24 : 0;
  const confettiColors =
    tier === "champion" ? ["#A855F7", "#E9D5FF", "#FFD700", "#00E5FF", "#FFFFFF"] :
    tier === "legendary" ? ["#FFD700", "#FFC83A", "#FFF3B0", "#FF3B30", "#FFFFFF"] :
    ["#FFD700", "#FFFFFF", "#FFC83A"];
  const hasFlash = isMega || tier === "walkout";
  const flashCount = tier === "champion" ? 3 : tier === "legendary" ? 2 : 1;
  const hasSparks = tier === "walkout" || isMega;

  const cardEnterAnimation = tier === "champion"
    ? "wo-card-enter-champ 1400ms cubic-bezier(.22,1.4,.36,1) forwards"
    : tier === "legendary"
      ? "wo-card-enter-legend 1300ms cubic-bezier(.22,1.4,.36,1) forwards"
      : "wo-card-enter 1200ms cubic-bezier(.22,1.4,.36,1) forwards";

  return (
    <div
      className="fixed inset-0 z-[10000] flex flex-col items-center justify-center text-white select-none overflow-hidden"
      onClick={next}
      data-testid="walkout-overlay"
      style={{
        background: tier === "champion"
          ? "radial-gradient(circle at center, rgba(168,85,247,0.28) 0%, rgba(20,5,40,0.95) 50%, #000 80%)"
          : tier === "legendary"
            ? "radial-gradient(circle at center, rgba(255,215,0,0.22) 0%, rgba(40,30,5,0.95) 50%, #000 80%)"
            : tier === "walkout"
              ? "radial-gradient(circle at center, rgba(255,200,58,0.14) 0%, #000 70%)"
              : tier === "downgrade"
                ? "radial-gradient(circle at center, rgba(255,59,48,0.12) 0%, #000 70%)"
                : "#000",
        animation: phase === "enter" && hasShake ? "wo-screen-shake 700ms ease-out 80ms" : undefined,
      }}
    >
      <style>{`
        @keyframes wo-card-enter {
          0%   { transform: translateZ(0) scale(0.2) rotate(-25deg); opacity: 0; filter: blur(20px); }
          40%  { opacity: 1; filter: blur(0); }
          70%  { transform: translateZ(0) scale(1.05) rotate(2deg); }
          100% { transform: translateZ(0) scale(1) rotate(0deg); opacity: 1; filter: blur(0); }
        }
        @keyframes wo-card-enter-legend {
          0%   { transform: scale(0.05) rotate(-360deg); opacity: 0; filter: blur(30px) brightness(2); }
          35%  { opacity: 1; filter: blur(0) brightness(1.4); }
          65%  { transform: scale(1.12) rotate(4deg); filter: brightness(1.15); }
          85%  { transform: scale(0.97) rotate(-1deg); }
          100% { transform: scale(1) rotate(0deg); opacity: 1; filter: blur(0) brightness(1); }
        }
        @keyframes wo-card-enter-champ {
          0%   { transform: scale(0.02) rotate(-540deg); opacity: 0; filter: blur(40px) brightness(2.5) hue-rotate(60deg); }
          25%  { opacity: 1; filter: blur(2px) brightness(1.7) hue-rotate(20deg); }
          55%  { transform: scale(1.2) rotate(8deg); filter: brightness(1.3) hue-rotate(0deg); }
          75%  { transform: scale(0.94) rotate(-3deg); }
          90%  { transform: scale(1.03) rotate(1deg); }
          100% { transform: scale(1) rotate(0deg); opacity: 1; filter: blur(0) brightness(1); }
        }
        @keyframes wo-card-exit {
          0%   { transform: scale(1) rotate(0); opacity: 1; }
          100% { transform: scale(0.7) translateY(-40px); opacity: 0; filter: blur(8px); }
        }
        @keyframes wo-flash {
          0%   { opacity: 0; }
          8%   { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes wo-glow-pulse {
          0%, 100% { opacity: 0.35; transform: scale(1); }
          50%      { opacity: 0.85; transform: scale(1.08); }
        }
        @keyframes wo-glow-pulse-mega {
          0%, 100% { opacity: 0.55; transform: scale(1); }
          50%      { opacity: 1; transform: scale(1.18); }
        }
        @keyframes wo-delta-pop {
          0%   { transform: scale(0); opacity: 0; }
          60%  { transform: scale(1.3); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes wo-rise {
          0%   { transform: translateY(20px); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
        @keyframes wo-confetti-fall {
          0%   { transform: translateY(-10vh) rotate(0deg); opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0.4; }
        }
        @keyframes wo-lightning {
          0%, 100% { opacity: 0; }
          5%, 8%, 14% { opacity: 1; }
          11%, 17% { opacity: 0; }
        }
        @keyframes wo-screen-shake {
          0%, 100% { transform: translate(0, 0); }
          10% { transform: translate(-6px, 4px); }
          20% { transform: translate(5px, -3px); }
          30% { transform: translate(-4px, -3px); }
          40% { transform: translate(3px, 4px); }
          50% { transform: translate(-3px, 2px); }
          60% { transform: translate(2px, -2px); }
          70% { transform: translate(-2px, 2px); }
          80% { transform: translate(2px, -1px); }
          90% { transform: translate(-1px, 1px); }
        }
        @keyframes wo-sunray-spin {
          0%   { transform: rotate(0deg) scale(0.4); opacity: 0; }
          25%  { opacity: 0.9; }
          100% { transform: rotate(360deg) scale(2.2); opacity: 0; }
        }
        @keyframes wo-firework-burst {
          0%   { transform: scale(0); opacity: 1; }
          70%  { opacity: 1; }
          100% { transform: scale(8); opacity: 0; }
        }
        @keyframes wo-spark {
          0%   { transform: translate(0,0) scale(1); opacity: 1; }
          100% { transform: translate(var(--sx), var(--sy)) scale(0); opacity: 0; }
        }
        @keyframes wo-crown-rise {
          0%   { transform: translateY(40px) scale(0.5); opacity: 0; filter: drop-shadow(0 0 0 transparent); }
          60%  { transform: translateY(-12px) scale(1.25); opacity: 1; filter: drop-shadow(0 0 24px #E9D5FF); }
          100% { transform: translateY(0) scale(1); opacity: 1; filter: drop-shadow(0 0 18px #E9D5FF); }
        }
      `}</style>

      {/* Top bar */}
      <div className="absolute top-4 left-0 right-0 flex items-center justify-between px-6 text-xs uppercase tracking-widest z-30" onClick={e => e.stopPropagation()}>
        <div className="text-neutral-400">
          {idx + 1} <span className="text-neutral-600">/</span> {total}
        </div>
        <div className="flex items-center gap-3">
          <div className="text-neutral-500">Klick · Leertaste = weiter · Esc = beenden</div>
          <button
            type="button"
            onClick={() => onClose?.()}
            className="px-3 py-1.5 border border-white/15 hover:border-white/40 text-neutral-300 hover:text-white"
            data-testid="walkout-close"
          >
            Schließen
          </button>
        </div>
      </div>

      {/* Multi-Flash bei mega/walkout */}
      {hasFlash && phase === "enter" && (
        <>
          {Array.from({ length: flashCount }).map((_, i) => (
            <div
              key={`flash-${i}`}
              className="absolute inset-0 bg-white pointer-events-none z-20"
              style={{ animation: `wo-flash ${500 + i * 80}ms ease-out ${i * 220}ms forwards` }}
            />
          ))}
        </>
      )}

      {/* Sunrays für legendary & champion */}
      {hasSunrays && phase !== "exit" && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden flex items-center justify-center">
          <div
            style={{
              position: "absolute",
              width: "200vmax",
              height: "200vmax",
              background: `repeating-conic-gradient(${tierColor}30 0deg 8deg, transparent 8deg 18deg)`,
              animation: "wo-sunray-spin 2.2s ease-out forwards",
              mixBlendMode: "screen",
            }}
          />
        </div>
      )}

      {/* Lightning streaks */}
      {hasLightning && phase !== "exit" && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {Array.from({ length: lightningCount }).map((_, i) => {
            const leftPct = (i * (100 / lightningCount) + (i % 2 === 0 ? 4 : 12)) % 100;
            return (
              <div
                key={`bolt-${i}`}
                style={{
                  position: "absolute",
                  top: 0, bottom: 0,
                  left: `${leftPct}%`,
                  width: tier === "champion" ? 3 : 2,
                  background: `linear-gradient(180deg, ${tierColor}00 0%, ${tierColor} 50%, ${tierColor}00 100%)`,
                  filter: `blur(${tier === "champion" ? 1.5 : 1}px)`,
                  animation: `wo-lightning ${1.2 + (i % 4) * 0.25}s ease-in-out ${(i % 5) * 0.12}s infinite`,
                  boxShadow: isMega ? `0 0 24px ${tierColor}` : "none",
                }}
              />
            );
          })}
        </div>
      )}

      {/* Firework bursts bei champion */}
      {hasFireworks && phase === "enter" && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[
            { x: "20%", y: "30%", color: "#A855F7", delay: 0 },
            { x: "78%", y: "28%", color: "#E9D5FF", delay: 280 },
            { x: "32%", y: "72%", color: "#FFD700", delay: 520 },
            { x: "70%", y: "78%", color: "#00E5FF", delay: 760 },
            { x: "50%", y: "20%", color: "#FFFFFF", delay: 1000 },
          ].map((fw, i) => (
            <div
              key={`fw-${i}`}
              style={{
                position: "absolute",
                left: fw.x,
                top: fw.y,
                width: 12,
                height: 12,
                borderRadius: "50%",
                background: fw.color,
                boxShadow: `0 0 30px ${fw.color}, 0 0 60px ${fw.color}80, 0 0 90px ${fw.color}40`,
                animation: `wo-firework-burst 900ms ease-out ${fw.delay}ms forwards`,
                opacity: 0,
              }}
            />
          ))}
        </div>
      )}

      {/* Konfetti */}
      {hasConfetti && phase !== "exit" && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {Array.from({ length: confettiCount }).map((_, i) => {
            const left = (i * 37) % 100;
            return (
              <div
                key={`confetti-${i}`}
                style={{
                  position: "absolute",
                  top: "-5vh",
                  left: `${left}%`,
                  width: tier === "champion" ? 10 : 8,
                  height: tier === "champion" ? 16 : 14,
                  background: confettiColors[i % confettiColors.length],
                  opacity: 0.92,
                  animation: `wo-confetti-fall ${2.2 + (i % 6) * 0.28}s linear ${(i % 9) * 0.08}s infinite`,
                  transform: `rotate(${(i * 47) % 360}deg)`,
                  boxShadow: tier === "champion" ? `0 0 8px ${confettiColors[i % confettiColors.length]}` : "none",
                }}
              />
            );
          })}
        </div>
      )}

      {/* Sparks rings um Karte */}
      {hasSparks && phase === "enter" && (
        <div className="absolute pointer-events-none" style={{ width: 1, height: 1 }}>
          {Array.from({ length: tier === "champion" ? 24 : tier === "legendary" ? 18 : 12 }).map((_, i) => {
            const angle = (i / (tier === "champion" ? 24 : tier === "legendary" ? 18 : 12)) * Math.PI * 2;
            const dist = tier === "champion" ? 320 : tier === "legendary" ? 260 : 200;
            const sx = Math.cos(angle) * dist;
            const sy = Math.sin(angle) * dist;
            return (
              <div
                key={`spark-${i}`}
                style={{
                  position: "absolute",
                  width: 6, height: 6,
                  borderRadius: "50%",
                  background: tierColorAlt,
                  boxShadow: `0 0 12px ${tierColor}, 0 0 24px ${tierColor}80`,
                  ["--sx"]: `${sx}px`,
                  ["--sy"]: `${sy}px`,
                  animation: `wo-spark 850ms ease-out ${250 + i * 22}ms forwards`,
                  opacity: 0,
                }}
              />
            );
          })}
        </div>
      )}

      {/* Glow hinter Karte */}
      {(isMega || tier === "walkout" || tier === "upgrade") && phase === "settled" && (
        <div
          className="absolute pointer-events-none"
          style={{
            width: isMega ? 480 : 360,
            height: isMega ? 600 : 480,
            borderRadius: 24,
            background: `radial-gradient(circle, ${tierColor}${isMega ? "88" : "55"} 0%, transparent 70%)`,
            filter: `blur(${isMega ? 60 : 40}px)`,
            animation: `${isMega ? "wo-glow-pulse-mega" : "wo-glow-pulse"} 1.8s ease-in-out infinite`,
          }}
        />
      )}

      {/* Champion Crown über Karte */}
      {tier === "champion" && phase === "settled" && (
        <div
          className="absolute z-10 pointer-events-none"
          style={{
            top: "calc(50% - 320px)",
            animation: "wo-crown-rise 900ms cubic-bezier(.34,1.56,.64,1) 100ms forwards",
            opacity: 0,
          }}
        >
          <Crown size={64} color="#E9D5FF" strokeWidth={2.5} fill="#A855F7" />
        </div>
      )}

      {/* DRIVER CARD */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          animation: phase === "exit" ? "wo-card-exit 320ms ease-in forwards" : cardEnterAnimation,
          transformStyle: "preserve-3d",
          willChange: "transform, opacity, filter",
          position: "relative",
          zIndex: 5,
          width: 288,
          height: 420,
        }}
      >
        <DriverCard
          driver={current.driver}
          team={current.team}
          prev={current.prev}
          archived={false}
        />

        {/* Gestaffelter Mask-Reveal nur bei legendary */}
        {tier === "legendary" && phase !== "exit" && (
          <StagedMasks stageStep={stageStep} tierColor={tierColor} tierColorAlt={tierColorAlt} />
        )}
      </div>

      {/* OVR-Wechsel + Delta (nur im Delta-Modus) */}
      {phase === "settled" && !revealMode && (
        <div
          className="mt-8 flex items-center gap-6 z-10"
          onClick={e => e.stopPropagation()}
          style={{ animation: "wo-rise 420ms ease-out forwards" }}
        >
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Alt</div>
            <div className="font-display font-black italic text-4xl text-neutral-400 leading-none mt-1">
              {current.oldOvr || "—"}
            </div>
          </div>

          <div className="text-2xl text-neutral-500">›</div>

          <div className="text-center">
            <div className="text-[10px] uppercase tracking-widest" style={{ color: tierColor }}>Neu</div>
            <div
              className="font-display font-black italic leading-none mt-1"
              style={{
                color: tierColor,
                fontSize: isMega ? 96 : tier === "walkout" ? 72 : 56,
                textShadow: isMega
                  ? `0 0 28px ${tierColor}, 0 0 72px ${tierColor}88`
                  : tier === "walkout"
                    ? `0 0 22px ${tierColor}aa`
                    : "none",
                animation: "wo-delta-pop 560ms cubic-bezier(.34,1.56,.64,1) forwards",
              }}
            >
              {current.newOvr || "—"}
            </div>
          </div>

          <div
            className="font-display font-black italic uppercase px-4 py-2"
            style={{
              color: tierColor,
              border: `2px solid ${tierColor}`,
              background: `${tierColor}11`,
              fontSize: isMega ? 38 : 24,
              letterSpacing: 1,
              animation: "wo-delta-pop 640ms cubic-bezier(.34,1.56,.64,1) 100ms forwards",
              opacity: 0,
              boxShadow: isMega
                ? `0 0 40px ${tierColor}aa, inset 0 0 24px ${tierColor}33`
                : tier === "walkout" ? `0 0 18px ${tierColor}66` : "none",
            }}
          >
            {delta > 0 ? `+${delta}` : delta}
          </div>
        </div>
      )}

      {/* Tier-Label */}
      {phase === "settled" && (revealMode || (tier !== "standard" && tier !== "upgrade")) && (
        <div
          className="mt-6 font-display font-black italic uppercase tracking-widest z-10"
          style={{
            color: tierColor,
            fontSize: isMega ? 44 : 24,
            textShadow: `0 0 22px ${tierColor}cc, 0 0 60px ${tierColor}66`,
            animation: "wo-rise 520ms ease-out 200ms both",
          }}
        >
          {revealMode
            ? (tier === "champion" ? "★ CHAMPION REVEAL ★"
              : tier === "legendary" ? "⚡ LEGENDARY REVEAL ⚡"
              : tier === "walkout" ? "✦ PREMIUM REVEAL ✦"
              : "Reveal")
            : (tier === "champion" ? "★ CHAMPION ★"
              : tier === "legendary" ? "⚡ LEGENDARY ⚡"
              : tier === "walkout" ? "Walkout"
              : tier === "downgrade" ? "Downgrade"
              : "")}
        </div>
      )}

      {/* Fußzeile mit Fahrer-Name */}
      <div
        className="absolute bottom-6 left-0 right-0 text-center z-10"
        onClick={e => e.stopPropagation()}
      >
        <div className="font-display font-black italic uppercase tracking-tight text-3xl">
          {current.driver?.name}
        </div>
        <div className="text-xs uppercase tracking-widest text-neutral-500 mt-1">
          {current.team?.name || ""}
        </div>
      </div>
    </div>
  );
}

/**
 * Gestaffelter Reveal-Mask-Layer (nur Legendary OVR ≥ 90).
 * 5 Stages enthüllen nacheinander: Werte → OVR → Land → Team → Bild+Name.
 * Positions-Pixel basieren auf 288 × 420px DriverCard-Layout.
 */
function StagedMasks({ stageStep, tierColor, tierColorAlt }) {
  // [stageThreshold, label, positionStyle]
  // Maske verschwindet wenn stageStep >= threshold
  const masks = [
    { threshold: 1, label: "WERTE", style: { bottom: 16, left: 16, right: 16, height: 56 } },
    { threshold: 2, label: "OVR",   style: { top: 16, left: 16, width: 92, height: 92 } },
    { threshold: 3, label: "LAND",  style: { top: 112, left: 16, width: 92, height: 28 } },
    { threshold: 4, label: "TEAM",  style: { top: 144, left: 16, width: 92, height: 88 } },
    // Stage 5 = zwei Bereiche: rechtes Portrait-Areal + Name-Streifen
    { threshold: 5, label: "PILOT", style: { top: 16, left: 112, right: 0, height: 282 }, key: "5a" },
    { threshold: 5, label: "NAME",  style: { bottom: 76, left: 16, right: 16, height: 28 }, key: "5b" },
  ];

  return (
    <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 50 }}>
      <style>{`
        @keyframes wo-mask-vanish {
          0%   { opacity: 1; transform: scale(1); filter: brightness(1); }
          40%  { opacity: 1; transform: scale(1.06); filter: brightness(2.2); }
          100% { opacity: 0; transform: scale(1.18); filter: brightness(2.6); }
        }
        @keyframes wo-mask-shimmer {
          0%, 100% { background-position: 0 0; }
          50%      { background-position: 40px 40px; }
        }
      `}</style>
      {masks.map((m) => {
        const isOpen = stageStep >= m.threshold;
        return (
          <div
            key={m.key || m.threshold}
            style={{
              position: "absolute",
              ...m.style,
              background: `linear-gradient(135deg, ${tierColorAlt} 0%, ${tierColor} 50%, #6b4a00 100%)`,
              backgroundImage: `repeating-linear-gradient(45deg, rgba(0,0,0,0.28) 0 2px, transparent 2px 12px), linear-gradient(135deg, ${tierColorAlt} 0%, ${tierColor} 50%, #6b4a00 100%)`,
              border: `2px solid ${tierColor}`,
              boxShadow: `0 0 18px ${tierColor}cc, inset 0 0 24px rgba(0,0,0,0.45)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#0a0a0a",
              fontFamily: "var(--font-display, 'Bebas Neue', 'Inter', sans-serif)",
              fontWeight: 900,
              fontStyle: "italic",
              fontSize: 12,
              letterSpacing: "0.2em",
              textShadow: `0 1px 0 ${tierColorAlt}`,
              animation: isOpen
                ? "wo-mask-vanish 520ms cubic-bezier(.5,0,.7,1) forwards"
                : "wo-mask-shimmer 1.6s ease-in-out infinite",
              willChange: "opacity, transform, filter",
            }}
            data-testid={`reveal-mask-${m.key || m.threshold}`}
          >
            {!isOpen && (
              <span style={{ position: "relative", color: "rgba(0,0,0,0.55)" }}>
                {m.label}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
